// ONYX-CUSTOM: shared selection store and <onyx-upsell> element for the product page upsell (main product block and section)
//
// Loaded with type="module", so it runs once however many snippets include it and its
// top-level names stay out of the global scope.

// One selection per page. Every <onyx-upsell> subscribes to it, so a tick in the block, in the
// section or a variant change in the main form all end up in the same place.
const state = {
  choices: new Map(),
  selected: new Set(),
  main: null,
  labels: null,
  original: null,
  hydrated: false,
  submitting: false,
  loading: false,
};

const listeners = new Set();

const mainInfo = () => document.querySelector('product-info[id^="MainProduct-"]');
const mainForm = () => mainInfo()?.querySelector('form[is="product-form"]');
const mainButton = () => mainInfo()?.querySelector('.product-form__submit');

const mainQuantity = () => {
  const quantity = parseInt(mainInfo()?.querySelector('input[name="quantity"]')?.value, 10);
  return quantity > 0 ? quantity : 1;
};

const formatMoney = (cents) => FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);

const withVariant = (title, variantTitle) => (variantTitle ? `${title} – ${variantTitle}` : title);

const total = () => {
  let sum = state.main.price * mainQuantity();
  state.selected.forEach((productId) => {
    sum += state.choices.get(productId).price;
  });
  return sum;
};

const buttonLabel = () => {
  if (state.selected.size === 0) return state.labels.single;
  return state.labels.many.replace(/__count__|\{count\}|%{1,2}count%{1,2}/, state.selected.size + 1);
};

const restoreButton = (button) => {
  if (!state.hydrated) return;

  const text = button.querySelector('.btn__text');
  const price = button.querySelector('.product-form__submit-price');
  if (text) text.textContent = state.original.text;
  if (price && state.original.price !== undefined) price.innerHTML = state.original.price;

  state.hydrated = false;
  state.original = null;
};

const hydrateButton = () => {
  const button = mainButton();
  if (!button) return;

  if (state.selected.size === 0 || button.disabled) {
    restoreButton(button);
    return;
  }

  const text = button.querySelector('.btn__text');
  const price = button.querySelector('.product-form__submit-price');
  if (!text) return;

  if (!state.hydrated) {
    state.original = { text: text.textContent, price: price?.innerHTML };
    state.hydrated = true;
  }

  if (price) {
    text.textContent = buttonLabel();
    price.textContent = formatMoney(total());
  } else {
    text.textContent = `${buttonLabel()} – ${formatMoney(total())}`;
  }
};

const notify = () => {
  hydrateButton();
  listeners.forEach((listener) => listener());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const finishSubmit = () => {
  state.submitting = false;
  state.loading = false;
  notify();
};

const watchMainButton = (button) => {
  const observer = new MutationObserver(() => {
    if (button.classList.contains('btn--loading')) return;
    observer.disconnect();
    finishSubmit();
  });
  observer.observe(button, { attributes: true, attributeFilter: ['class'] });
};

const submitMainForm = () => {
  const form = mainForm();
  const button = mainButton();
  if (!form || !button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

  state.loading = true;
  notify();
  watchMainButton(button);
  form.requestSubmit(button);
};

// ProductForm builds its FormData from the form on submit, so the form is rewritten to the
// `items[]` shape for that one dispatch and put back right after. The vendor submit code and
// the cart:refresh / drawer flow stay untouched.
const injectItems = (form) => {
  const data = new FormData(form);
  const added = [];
  const disabled = [];

  const addInput = (name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.append(input);
    added.push(input);
  };

  addInput('items[0][id]', data.get('id'));
  addInput('items[0][quantity]', mainQuantity());

  for (const [key, value] of data) {
    const property = key.match(/^properties\[(.+)\]$/);
    if (key === 'selling_plan') addInput('items[0][selling_plan]', value);
    if (property) addInput(`items[0][properties][${property[1]}]`, value);
  }

  let index = 1;
  state.selected.forEach((productId) => {
    addInput(`items[${index}][id]`, state.choices.get(productId).variantId);
    addInput(`items[${index}][quantity]`, 1);
    // Hidden (leading underscore) so the cart and drawer skip it; same property the vendor theme writes
    addInput(`items[${index}][properties][_AddedFrom]`, state.choices.get(productId).addedFrom);
    const { tracking } = state.choices.get(productId);
    if (tracking) addInput(`items[${index}][properties][_tracking]`, tracking);
    index++;
  });

  form.querySelectorAll('[name="id"], [name="quantity"], [name="selling_plan"], [name^="properties["]').forEach((el) => {
    if (el.disabled) return;
    el.disabled = true;
    disabled.push(el);
  });

  setTimeout(() => {
    disabled.forEach((el) => (el.disabled = false));
    added.forEach((el) => el.remove());
  });

  state.submitting = true;
};

class OnyxUpsell extends HTMLElement {
  connectedCallback() {
    if (!state.main) {
      state.main = {
        productId: this.dataset.mainProductId,
        variantId: this.dataset.mainVariantId,
        price: parseInt(this.dataset.mainPrice, 10),
        title: this.dataset.mainTitle,
        variantTitle: this.dataset.mainVariantTitle,
      };
    }

    if (!state.labels) {
      state.labels = { single: this.dataset.labelSingle, many: this.dataset.labelMany };
    }

    this.querySelectorAll('[data-onyx-upsell-item]').forEach((item) => {
      const productId = item.dataset.productId;
      if (state.choices.has(productId)) return;

      state.choices.set(productId, {
        variantId: item.dataset.variantId,
        price: parseInt(item.dataset.price, 10),
        comparePrice: parseInt(item.dataset.comparePrice, 10) || 0,
        title: item.dataset.title,
        variantTitle: item.dataset.variantTitle,
        addedFrom: item.closest('onyx-upsell').dataset.addedFrom,
        tracking: item.dataset.onyxTracking,
      });
    });

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.unsubscribe = subscribe(this.render);
    notify();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    this.unsubscribe?.();
  }

  onChange = (event) => {
    const item = event.target.closest('[data-onyx-upsell-item]');
    if (!item) return;

    const productId = item.dataset.productId;

    if (event.target.matches('input[type="checkbox"]')) {
      if (event.target.checked) {
        state.selected.add(productId);
      } else {
        state.selected.delete(productId);
      }
    }

    if (event.target.matches('select')) {
      const option = event.target.selectedOptions[0];
      state.choices.set(productId, {
        ...state.choices.get(productId),
        variantId: option.value,
        price: parseInt(option.dataset.price, 10),
        comparePrice: parseInt(option.dataset.comparePrice, 10) || 0,
        variantTitle: option.dataset.title,
      });
    }

    notify();
  };

  onClick = (event) => {
    if (event.target.closest('[data-onyx-submit]')) submitMainForm();
  };

  render = () => {
    this.querySelectorAll('[data-onyx-upsell-item]').forEach((item) => {
      const productId = item.dataset.productId;
      const choice = state.choices.get(productId);
      const isSelected = state.selected.has(productId);

      item.classList.toggle('is-selected', isSelected);
      item.querySelector('input[type="checkbox"]').checked = isSelected;

      const select = item.querySelector('select');
      if (select) select.value = choice.variantId;

      item.querySelectorAll('[data-onyx-price]').forEach((el) => {
        el.textContent = formatMoney(choice.price);
      });

      item.querySelectorAll('[data-onyx-compare]').forEach((el) => {
        el.hidden = !(choice.comparePrice > choice.price);
        el.textContent = formatMoney(choice.comparePrice);
      });
    });

    this.querySelectorAll('[data-onyx-submit]').forEach((el) => {
      el.classList.toggle('btn--loading', state.loading);
      el.toggleAttribute('aria-disabled', state.loading);
    });

    const status = this.querySelector('[data-onyx-status]');
    if (status) this.renderStatus(status);
  };

  renderStatus(status) {
    const entries = [
      {
        name: withVariant(state.main.title, state.main.variantTitle),
        price: state.main.price * mainQuantity(),
      },
    ];
    state.selected.forEach((productId) => {
      const choice = state.choices.get(productId);
      entries.push({ name: withVariant(choice.title, choice.variantTitle), price: choice.price });
    });

    status.querySelector('[data-onyx-list]').replaceChildren(
      ...entries.map((entry) => {
        const li = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('span');
        name.textContent = entry.name;
        price.textContent = formatMoney(entry.price);
        li.append(name, price);
        return li;
      })
    );

    status.querySelector('[data-onyx-count]').textContent = entries.length;
    status.querySelector('[data-onyx-total]').textContent = formatMoney(total());

    const submit = status.querySelector('[data-onyx-submit]');
    submit.querySelector('.btn__text').textContent = buttonLabel();
    const submitPrice = submit.querySelector('[data-onyx-button-price]');
    if (submitPrice) submitPrice.textContent = formatMoney(total());
    submit.disabled = mainButton()?.disabled ?? true;
  }
}

if (!customElements.get('onyx-upsell')) {
  customElements.define('onyx-upsell', OnyxUpsell);
}

document.addEventListener(
  'submit',
  (event) => {
    if (event.target !== mainForm() || state.selected.size === 0) return;
    injectItems(event.target);
  },
  true
);

// product-info.js has already swapped the button markup for the new variant when this fires,
// so what is in the DOM now is the untouched server version again.
document.addEventListener('variant:changed', (event) => {
  const variant = event.detail?.variant;
  if (!variant || !state.main) return;

  const variantTitle = variant.public_title || (variant.title !== 'Default Title' ? variant.title : '');
  state.main = { ...state.main, variantId: variant.id, price: variant.price, variantTitle };
  state.hydrated = false;
  state.original = null;
  notify();
});

document.addEventListener('change', (event) => {
  if (event.target.matches('input[name="quantity"]') && mainInfo()?.contains(event.target) && state.main) notify();
});

document.addEventListener('product-ajax:added', () => {
  if (!state.submitting) return;
  state.selected.clear();
  finishSubmit();
});
